import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import {
  clearConsumablesMap,
  getConsumables,
  selectState,
  setConsumablesMap,
  setServiceRepairPopover,
} from "../serviceAndRepair.slice";
import classes from "../../../styles/ServiceRepairModal.module.scss";
import classesPop from "../../../../../../components/Popover/popover.module.scss";
import Loader from "components/Loader/Loader";
import { FormatDate, FormatDateIso } from "utils/dateUtils";
import { useCallback, useEffect, useState } from "react";
import { ServiceType } from "Dashboard/scenes/Resources/domain/ServiceType";
import { ICreateServiceRequestParams } from "Dashboard/scenes/Resources/domain/ICreateServiceRequestParams";
import { Controller, useForm } from "react-hook-form";
import {
  selectResourceDetails,
  updateLastKnownUsageAmount,
  updateNextServiceRecommendedAt
} from "../../Details/details.slice";
import { IConsumableItem } from "Dashboard/scenes/Resources/domain/IConsumableItem";
import { resourcesServices } from "Dashboard/scenes/Resources/services/resource.service";
import { toast } from "react-toastify";
import { unitName } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { IInput } from "Dashboard/scenes/Consumables/domain/IInput";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import InfoTooltip from "components/Popup/InfoTooltip";
import { copyToClipboard } from "utils/clipboardUtils";
import { consumablesService } from "Dashboard/scenes/Consumables/services/consumables.service";
import NameAsSearch from "./NameAsSearch";
import NumberInput from "Dashboard/components/NumberInput";
import DatePicker from "react-datepicker";

interface IProps {
  onModalChange: Function;
}

const ServiceRepairPopover = ({ onModalChange }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    serviceRepairPopover,
    consumablesLoading,
    consumables,
    consumablesMap,
  } = useSelector(selectState);

  const resourceDetails = useSelector(selectResourceDetails);

  const [serviceType, setServiceType] = useState<ServiceType>(
    ServiceType.REPAIR
  );

  const [clickedTimes, setClickedTimes] = useState(0);
  const [emptyInputsWarning, setEmptyInputsWarning]
    = useState<boolean>(false);
  const [incorrectServiceDoneAtUsageAmount, setIncorrectServiceDoneAtUsageAmount]
    = useState<boolean>(false);
  const [lowNextServiceRecommendedAtUsageAmount, setLowNextServiceRecommendedAtUsageAmount]
    = useState<boolean>(false);
  const [currentServiceDoneAtUsageAmount, setCurrentServiceDoneAtUsageAmount]
    = useState<number>(0);
  const [currentNextServiceRecommendedAtUsageAmount, setCurrentNextServiceRecommendedAtUsageAmount]
    = useState<number>(0);
  const [billOfLandingSequence, setBillOfLandingSequence]
    = useState(0);
  const [requisitionSequence, setRequisitionSequence] = useState(0);

  const loadConsumables = useCallback(() => {
    dispatch(getConsumables());
  }, [dispatch]);

  useEffect(() => {
    if (serviceRepairPopover.isOpen) {
      setClickedTimes(0);
      loadConsumables();
      loadRepositorySequences();
    }
  }, [serviceRepairPopover, loadConsumables]);

  const loadRepositorySequences = async () => {
    const rep = await consumablesService.getRepository();
    setBillOfLandingSequence(rep.billOfLandingSequence);
    setRequisitionSequence(rep.requisitionSequence);
  }

  const getConsumableItems = useCallback(() => {
    const consumableItems: IConsumableItem[] = [];
    for (const [key, value] of Object.entries(consumablesMap)) {
      consumableItems.push({
        consumableInputId: Number(key),
        quantityAmount: value || 0,
      });
    }
    return consumableItems;
  }, [consumablesMap]);

  const close = () => {
    dispatch(setServiceRepairPopover({ isOpen: false }));
    dispatch(clearConsumablesMap());
  };

  const onChange =
    (consumableInputId: number) => (newValue: number | undefined) => {
      dispatch(
        setConsumablesMap({
          consumableInputId: consumableInputId,
          quantityAmount: newValue,
        })
      );
    };

  const onChangeServiceDoneAtUsageAmount = (newValue: number) => {
    setCurrentServiceDoneAtUsageAmount(newValue);
    if (resourceDetails && clickedTimes > 0) {
      setIncorrectServiceDoneAtUsageAmount(
        newValue > resourceDetails?.resource.lastKnownUsageAmount);
    }
  }

  const onChangeNextServiceRecommendedAtUsageAmount = (newValue: number) => {
    setCurrentNextServiceRecommendedAtUsageAmount(newValue);
    if (resourceDetails && clickedTimes > 0) {
      setLowNextServiceRecommendedAtUsageAmount(
        newValue <= currentServiceDoneAtUsageAmount);
    }
  }

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm<ICreateServiceRequestParams>();

  const firstClick = () => {
    setClickedTimes(clickedTimes + 1);
    const items = getConsumableItems();
    items.length <= 0
      ? setEmptyInputsWarning(true) : setEmptyInputsWarning(false);
    if (resourceDetails) {
      if (currentServiceDoneAtUsageAmount
        <= resourceDetails.resource.lastKnownUsageAmount) {
        setIncorrectServiceDoneAtUsageAmount(true);
      }
    }
  }

  const onSubmit = async (data: ICreateServiceRequestParams) => {
    const items = getConsumableItems();

    if (resourceDetails) {
      if (resourceDetails?.resource.lastKnownUsageAmount < currentServiceDoneAtUsageAmount) {
        setIncorrectServiceDoneAtUsageAmount(true);
        toast.error(`Unesite za 'urađen na' manju ${resourceDetails?.resource.usageUnit.name === "km"
          ? "kilometražu" : "satnicu"} ili jednaku poslednjoj unetoj (${FormatNumberWithUnitOrNull(resourceDetails?.resource.lastKnownUsageAmount, resourceDetails?.resource.usageUnit.name)})!`);
        return;
      }
    }

    if (resourceDetails && resourceDetails.resource.usageUnit !== null && serviceType === ServiceType.SERVICE) {
      if (currentServiceDoneAtUsageAmount >= currentNextServiceRecommendedAtUsageAmount) {
        setLowNextServiceRecommendedAtUsageAmount(true);
        toast.error(`Unesite za 'sledeći na' veću ${resourceDetails.resource.usageUnit.name === "km"
          ? "kilometražu" : "satnicu"} od 'urađen na'!`);
        return;
      }
    }

    if (resourceDetails && items.length > 0) {
      try {
        data.consumables = items;
        data.serviceType = serviceType;
        if (serviceType === ServiceType.REPAIR) {
          data.nextServiceRecommendedAtUsageAmount = undefined;
          data.nextServiceRecommendedAtDate = undefined;
        }
        await resourcesServices.createServiceForResource(
          resourceDetails?.resource.resourceId,
          data
        );
        dispatch(updateLastKnownUsageAmount(data.serviceDoneAtUsageAmount));
        if (data.nextServiceRecommendedAtUsageAmount) {
          dispatch(
            updateNextServiceRecommendedAt(data)
          );
        }
        toast.success("Unos servisa/popravke je uspesno unet.");
        onModalChange();
        close();
      } catch (err: any) {
        if (err.response.status === 409) {
          toast.error(`${data.serviceType === 'SERVICE' ? "Servis" : "Popravka"} sa istom ${resourceDetails.resource.usageUnit.name === "km" ? "kilometražom" : "satnicom"} je već unet!`);
        } else {
          toast.error("Neočekivana greška, proverite da li ste sve dobro uneli.");
        }
        console.error(err);
      }
    }
  };

  const handleInputBlur = (
    newValue: number,
    consumable: IInput
  ) => {
    const inputValue = newValue;
    const remainingValue = consumable.quantityAmountRemaining || 0;
    let finalValue: number | undefined = inputValue;

    if (inputValue > remainingValue) {
      finalValue = remainingValue > 0 ? remainingValue : undefined;
      const overflowValue = Math.round((inputValue - remainingValue) * 100) / 100.0;
      toast.info(
        `Nema ${inputValue} na lageru. Upisujem ${remainingValue}, a nedostajućih ${overflowValue} upisujem u klipboard.`
      );
      copyToClipboard((overflowValue).toString());
    }
    onChange(consumable.inputId)(finalValue);
  };

  useEffect(() => {
    if (clickedTimes > 0) {
      const items = getConsumableItems();
      setEmptyInputsWarning(items.length <= 0);
    }
  }, [clickedTimes, getConsumableItems])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`${classes.modalContent}`}
    >
      <div>
        <div className={classes.content}>
          <div className={classes.table}>
            <div className={`g-grid g-header ${classes.gHeader}`}>
              <div className={`row ${classes.row}`}>
                <div className="col center lowercase separator-line">
                  br. otpr. rač.
                </div>
                <div className="col center lowercase separator-line"><NameAsSearch /></div>
                <div className="col center lowercase separator-line">
                  količina
                </div>
                <div className="col center lowercase separator-line">
                  datum ulaza
                </div>
                <div className="col center lowercase separator-line">
                  kol. ostalo
                </div>
                <div className="col center uppercase">IZDAJEM</div>
                <div></div>
              </div>
            </div>
            <div
              className={`g-grid g-content ${classes.gContent} loading-container`}
            >
              {consumablesLoading && <Loader />}
              {consumables.map((consumable, index) => (
                <div className={`row ${classes.row}`} key={index}>
                  <div className={`col ${classes.col} capitalize light`}>
                    <div className="bg-item overflowMinWidth right">
                      <InfoTooltip
                        classesProp="bg-text-overflow"
                        tooltip={consumable.billOfLandingId.toString()}>
                        {consumable.billOfLandingId}
                      </InfoTooltip>
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize light`}>
                    <div className="bg-item overflowMinWidth center">
                      <InfoTooltip
                        classesProp={"bg-text-overflow"}
                        tooltip={`${consumable.item.name} - ${consumable.supplier.shortName}`}>
                        {consumable.item.name}
                      </InfoTooltip>
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize light`}>
                    <div className="bg-item right">
                      <span>
                        {FormatNumber(consumable.quantityAmountTotal)}
                      </span>
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize light center`}>
                    <div className="bg-item center">
                      {FormatDate(consumable.date)}
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize strong`}>
                    <div className="bg-item right">
                      <span>
                        {FormatNumber(consumable.quantityAmountRemaining)}
                      </span>
                    </div>
                  </div>

                  <div className={`col ${classes.col} uppercase light center`}>
                    <NumberInput
                      formatAs="quantity"
                      value={consumablesMap[consumable.inputId]?.toString() || ""}
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        onChange(consumable.inputId)(parsedValue);
                      }}
                      onBlur={(newValue: number) => handleInputBlur(newValue, consumable)}
                      className={`bg-item-dark center
                        ${emptyInputsWarning ? classes.bgItemError : ""}`}
                    />
                  </div>
                  <div className={classes.check}>
                    {consumablesMap[consumable.inputId] !== undefined &&
                      consumable.quantityAmountRemaining !== undefined &&
                      consumablesMap[consumable.inputId]! <=
                      consumable.quantityAmountRemaining && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={`${classes.options} ${classesPop.basicPopover}`}>
            <div className={`${classesPop.row} ${classesPop.rowDir} ${classes.flexStart}`}>
              <div className={`${classesPop.col} ${classesPop.colDir}`}>
                <div className={classes.serviceType}>
                  <span>
                    <input
                      type="radio"
                      value={ServiceType.SERVICE}
                      checked={serviceType === ServiceType.SERVICE}
                      onChange={() => {
                        setServiceType(ServiceType.SERVICE);
                      }}
                      aria-label="Servis"
                    />
                    <label>Servis</label>
                  </span>
                  <span>
                    <input
                      type="radio"
                      value={ServiceType.REPAIR}
                      checked={serviceType === ServiceType.REPAIR}
                      onChange={() => {
                        setServiceType(ServiceType.REPAIR);
                      }}
                    />
                    <label>Popravka</label>
                  </span>
                </div>
              </div>
              <div className={`${classesPop.col} ${classesPop.colDir} ${classes.sequence}`}>
                <label className={classesPop.aboveLabel}>sledeći br treb</label>
                <input type="text" className={`${classesPop.itemBorder} ${classesPop.transparent}
                    ${classesPop.textMedium} ${classesPop.center}`}
                  value={billOfLandingSequence} disabled />
              </div>

              <div className={`${classesPop.col} ${classesPop.colDir} ${classes.sequence}`}>
                <label className={classesPop.aboveLabel}>datum</label>
                <Controller
                  control={control}
                  name="serviceDoneAtDate"
                  defaultValue={FormatDateIso(new Date())}
                  rules={{ required: false, }}
                  render={({ field: { onChange, value } }) => (
                    <div>
                      <DatePicker
                        onChange={(date) =>
                          onChange(date ? FormatDateIso(date) : undefined)
                        }
                        dateFormat="dd.MM.yyyy"
                        showMonthDropdown
                        showYearDropdown
                        selected={value ? new Date(value) : null}
                        className={`${classesPop.dateItem} ${classesPop.itemBorder}
                            ${classes.datePicker}
                            ${errors.serviceDoneAtDate ? classesPop.itemError : ""}`}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
            {resourceDetails?.resource.usageUnit !== null && (
              <div className={`${classesPop.row} ${classesPop.rowDir} ${classes.flexStart}`}>
                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={`${classesPop.aboveLabel}`}>jed mere</label>
                  <input
                    type="text"
                    className={`${classesPop.itemBorder} ${classesPop.transparent}
                    ${classesPop.textMedium} ${classesPop.center}`}
                    value={resourceDetails && unitName(resourceDetails?.resource.usageUnit)}
                    disabled
                  />
                </div>
                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}>urađen na</label>
                  <Controller
                    name="serviceDoneAtUsageAmount"
                    control={control}
                    defaultValue={undefined}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange } }) => (
                      <NumberInput
                        formatAs="quantity"
                        onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                          onChange({ target: { value: parsedValue } });
                          onChangeServiceDoneAtUsageAmount(parsedValue);
                        }}
                        placeholder={resourceDetails?.resource.lastKnownUsageAmount !== undefined
                          ? FormatNumber(resourceDetails.resource.lastKnownUsageAmount)
                          : "0"}
                        className={`${classesPop.itemBorder} ${classesPop.center}
                        ${errors.serviceDoneAtUsageAmount || incorrectServiceDoneAtUsageAmount === true
                            ? classesPop.itemError : ""}`}
                      />
                    )}
                  />
                </div>
                {serviceType === ServiceType.SERVICE && (
                  <div className={`${classesPop.col} ${classesPop.colDir}`}>
                    <label className={classesPop.aboveLabel}>sledeći na</label>
                    <Controller
                      name="nextServiceRecommendedAtUsageAmount"
                      control={control}
                      defaultValue={undefined}
                      rules={{
                        required: true,
                      }}
                      render={({ field: { onChange } }) => (
                        <NumberInput
                          formatAs="quantity"
                          onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                            onChange({ target: { value: parsedValue } });
                            onChangeNextServiceRecommendedAtUsageAmount(parsedValue);
                          }}
                          placeholder={resourceDetails?.resource.nextServiceUsageAmount !== undefined
                            ? FormatNumber(resourceDetails.resource.nextServiceUsageAmount)
                            : "0"}
                          className={`${classesPop.itemBorder} ${classesPop.center}
                        ${errors.nextServiceRecommendedAtUsageAmount || lowNextServiceRecommendedAtUsageAmount === true
                              ? classesPop.itemError : ""}`}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            <div className={`${classesPop.row}`}>
              <div className={`${classesPop.col} ${classesPop.colDir}`}>
                <label className={classesPop.aboveLabel}>napomena</label>
                <div className={`${classesPop.col} ${classesPop.rowDir} 
                  ${classesPop.spaceBetween} ${classesPop.alignCenter}`}>
                  <textarea
                    {...register("note")}
                  ></textarea>

                  <div className={`${classesPop.buttons}`}>
                    <button type="submit"
                      onClick={() => clickedTimes === 0
                        ? firstClick() : null}
                      className={classesPop.iconButton}>
                      <FontAwesomeIcon
                        icon={faLongArrowAltDown}
                        className={classesPop.icon}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </form >
  );
};

export default ServiceRepairPopover;
